import React, { useCallback } from "react";
import { PushNotification } from "../../../types";
import { Button } from "../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "../../common/Grid";
import { Input, Label } from "../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import { languages } from "../../../config/config";

const now = new Date();
type Props = {
  isSubmitting: boolean;
  onSubmit: (values: PushNotification) => void;
  onCancel: () => void;
};

const PushNotificationForm: React.FC<Props> = ({
  isSubmitting,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();

  const createLabelsObject = useCallback((array: any) => {
    return array.reduce((acc: any, obj: any) => {
      return {
        ...acc,
        [obj.lang]: "",
      };
    }, {});
  }, []);

  const createLabelsYupObject = useCallback(
    (
      array: any,
      message: string,
      maxLength: number,
      maxLengthMessage: string
    ) => {
      return array.reduce((acc: any, obj: any) => {
        return obj.lang === "en" || obj.lang === "it"
          ? {
              ...acc,
              [obj.lang]: Yup.string()
                .required(message)
                .max(maxLength, maxLengthMessage),
            }
          : { ...acc };
      }, {});
    },
    []
  );

  const formik = useFormik({
    initialValues: {
      title: createLabelsObject(languages),
      message: createLabelsObject(languages),
      createdAt: now,
    },
    enableReinitialize: true,
    onSubmit: onSubmit,
    validationSchema: Yup.object().shape({
      title: Yup.object().shape(
        createLabelsYupObject(
          languages,
          t(
            "components.screenPushNotifications.pushNotificationsForm.titleRequired"
          ),
          50,
          t(
            "components.screenPushNotifications.pushNotificationsForm.titleMaxLength"
          )
        )
      ),
      message: Yup.object().shape(
        createLabelsYupObject(
          languages,
          t(
            "components.screenPushNotifications.pushNotificationsForm.messageRequired"
          ),
          150,
          t(
            "components.screenPushNotifications.pushNotificationsForm.messageMaxLength"
          )
        )
      ),
    }),
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Container>
        {languages.map((language: any) => {
          const lang = language.lang;
          return (
            <Row paddingTop="10px" key={language.lang}>
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor={`title.${lang}`}>
                    {t(
                      "components.screenPushNotifications.pushNotificationsForm.title"
                    )}
                    {" - "}
                    {language.description}
                  </Label>
                  <Input
                    id={`title.${lang}`}
                    name={`title.${lang}`}
                    placeholder={t(
                      "components.screenPushNotifications.pushNotificationsForm.titlePlaceholder"
                    )}
                    value={formik.values.title[lang] as unknown as string}
                    onChange={formik.handleChange}
                    state={formik.errors.title?.[lang] ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta(`title.${lang}`).error}
                  />
                </FormControl>
              </Col>
            </Row>
          );
        })}
        {languages.map((language: any) => {
          const lang = language.lang;
          return (
            <Row paddingTop="10px" key={language.lang}>
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor={`message.${lang}`}>
                    {t(
                      "components.screenPushNotifications.pushNotificationsForm.message"
                    )}
                    {" - "}
                    {language.description}
                  </Label>
                  <Input
                    id={`message.${lang}`}
                    name={`message.${lang}`}
                    placeholder={t(
                      "components.screenPushNotifications.pushNotificationsForm.messagePlaceholder"
                    )}
                    value={formik.values.message[lang] as unknown as string}
                    onChange={formik.handleChange}
                    state={formik.errors.message?.[lang] ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta(`message.${lang}`).error}
                  />
                </FormControl>
              </Col>
            </Row>
          );
        })}
      </Container>

      <Row paddingTop={"20px"}>
        <Col textAlign="end">
          <Button
            type={"reset"}
            onClick={onCancel}
            marginRight="10px"
            color="light"
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button type={"submit"}>{t("common.save")}</Button>
        </Col>
      </Row>
    </form>
  );
};

export default PushNotificationForm;
